
import { DataBehindTable, FvAndPpChartsTableData } from '../../pojo/fmv-charts-classes';
import {
  CategoryData,
  DrilldownCompanyData,
  GroupedPortfolio,
  PortfoliosPerformanceBarChartData,
} from '../../../../../chart/ChartAuxiliary';

export class FairValueAndPpChartsService {

  static generateDrilldownSeries(drilldownData: GroupedPortfolio, multiplicator = 1): any[] {
    if (!drilldownData) {
      return;
    }
    const drilldownSeries: any[] = [];

    Object.entries(drilldownData).forEach(([companyGroup, group]) => {
      // Sort the group data by investment + gain
      const sortedGroupData = group.data.sort((a, b) => {
        const aSum = (a.cost / MIL) + (a.gain / MIL);
        const bSum = (b.cost / MIL) + (b.gain / MIL);
        return aSum - bSum;
      });
      const gainData = sortedGroupData.map((item) => [item.companyData.name, Number((multiplicator * item.gain / MIL).toFixed(2))]);
      const investmentData = sortedGroupData.map((item) => [item.companyData.name, Number((multiplicator * item.cost / MIL).toFixed(2))]);
      const moicData = sortedGroupData.map((item) => [item.companyData.name, Number(item.cost ? ((item.gain + item.cost) / item.cost).toFixed(2) : 0)]);

      drilldownSeries.push({
        name: 'Gain/(loss)',
        id: companyGroup + 'gain',
        data: gainData,
        yAxis: 0,
      });

      drilldownSeries.push({
        name: 'Investment',
        id: companyGroup + 'inv',
        data: investmentData,
        yAxis: 0,
      });

      drilldownSeries.push({
        name: 'MOIC',
        id: companyGroup + 'moic',
        data: moicData,
        yAxis: 1,
        type: 'spline',
        zIndex: 10,
        lineWidth: 0,
      });

    });

    return drilldownSeries;
  }


  static processDataBehindTable(rawData: GroupedPortfolio, isOnlyCompanies = false): DataBehindTable {
    const data = new DataBehindTable();

    // First iteration: Calculate totals
    let totalCost = 0;
    let totalFairValue = 0;

    for (const category in rawData) {
      if (Object.prototype.hasOwnProperty.call(rawData, category)) {
        totalCost += this.calculateTotal(rawData[category], CalculationKey.COST);
        totalFairValue += this.calculateTotal(rawData[category], CalculationKey.FAIR_VALUE);
      }
    }

    const sortedCategories = Object.keys(rawData)
      .map((category) => ({
        categoryData: rawData[category],
        fairValue: this.calculateTotal(rawData[category], CalculationKey.FAIR_VALUE),
      }))
      .sort((a, b) => b.fairValue - a.fairValue);

    for (const { categoryData } of sortedCategories) {
      const mainRowData = this.createMainRowData(categoryData, totalCost, totalFairValue);
      const drilldownData = this.createDrilldownData(categoryData, totalCost, totalFairValue);
      data.body.push({ type: mainRowData, drilldown: drilldownData });
    }

    // Set the total row data
    data.total.cost = totalCost;
    data.total.fairValue = totalFairValue;
    data.total.gain = data.total.fairValue - data.total.cost;
    data.total.moic = data.total.fairValue / data.total.cost;
    return isOnlyCompanies ? this.processDataBehindTableForCompaniesOnly(data) : data;
  }

  static calculateTotal(categoryData: CategoryData, key: CalculationKey): number {
    return categoryData.data.reduce((sum, company) => {
      switch (key) {
        case CalculationKey.COST:
          return sum + company.cost;
        case CalculationKey.FAIR_VALUE:
          return sum + (company.cost + company.gain);
        case CalculationKey.GAIN:
          return sum + company.gain;
        default:
          return sum; // In case an unhandled key is passed, this line will prevent any changes to sum.
      }
    }, 0);
  }


  static createMainRowData(categoryData: CategoryData, totalCost: number, totalFairValue: number): FvAndPpChartsTableData {
    const mainRowData = new FvAndPpChartsTableData();
    mainRowData.companyData = new DrilldownCompanyData();
    mainRowData.companyData.name = categoryData.name;
    mainRowData.cost = this.calculateTotal(categoryData, CalculationKey.COST);
    mainRowData.fairValue = this.calculateTotal(categoryData, CalculationKey.FAIR_VALUE);
    mainRowData.gain = this.calculateTotal(categoryData, CalculationKey.GAIN);
    mainRowData.moic = mainRowData.fairValue / mainRowData.cost;
    mainRowData.pcntOfTotalCost = (mainRowData.cost / totalCost) * 100;
    mainRowData.pcntOfTotalFmv = (mainRowData.fairValue / totalFairValue) * 100;
    return mainRowData;
  }

  static createDrilldownData(categoryData: CategoryData, totalCost: number, totalFairValue: number): FvAndPpChartsTableData[] {
    return categoryData.data
      .map((company) => {
        const drilldown = new FvAndPpChartsTableData();
        drilldown.companyData = new DrilldownCompanyData();
        drilldown.companyData.name = company.companyData.name;
        drilldown.companyData.id = company.companyData.id;
        drilldown.cost = company.cost;
        drilldown.fairValue = company.cost + company.gain;
        drilldown.gain = company.gain;
        drilldown.moic = (company.cost + company.gain) / company.cost;
        drilldown.pcntOfTotalCost = (company.cost / totalCost) * 100;
        drilldown.pcntOfTotalFmv = ((company.cost + company.gain) / totalFairValue) * 100;
        return drilldown;
      })
      .sort((a, b) => b.fairValue - a.fairValue);
  }

  static processDataBehindTableForCompaniesOnly(data: DataBehindTable): DataBehindTable {
    const drillDown = data.body.flatMap((dr) => dr.drilldown.map((x) => (x)));
    const sortedDrillDown = drillDown.sort((a, b) => b.fairValue - a.fairValue);

    const dataUpdated = new DataBehindTable();
    dataUpdated.body.push({
      type: {
        companyData: { name: 'Companies', id: null },
        cost: -1,
        fairValue: -1,
        gain: -1,
        moic: -1,
        pcntOfTotalCost: -1,
        pcntOfTotalFmv: -1,
      },
      drilldown: sortedDrillDown,
    });
    dataUpdated.total = data.total;
    return dataUpdated;
  }
}

export class FmvPieData {
  byFund: FairValuePieChartsData[] = [];
  byCompany: FairValuePieChartsData[] = [];
  byIndustry: FairValuePieChartsData[] = [];
  byCountry: FairValuePieChartsData[] = [];
  byGroup: FairValuePieChartsData[] = [];
  byAssets: FairValuePieChartsData[] = [];
}

export class FmvBarData {
  byFund: PortfoliosPerformanceBarChartData;
  byCompany: FairValueByCompanyBarChartsData[] = [];
  byIndustry: PortfoliosPerformanceBarChartData;
  byCountry: PortfoliosPerformanceBarChartData;
  byGroup: PortfoliosPerformanceBarChartData;
  byAssets: PortfoliosPerformanceBarChartData;
}

export class FairValueByCompanyBarChartsData {
  companyData: DrilldownCompanyData;
  gain: number;
  cost: number;
  moic: number;
}

export class FairValuePieChartsData {
  name: string;
  y: number;
}

export const MIL = 1000000;
export const HUNDRED_THOUSANDS = 100000;

enum CalculationKey {
  COST = 'cost',
  FAIR_VALUE = 'fairValue',
  GAIN = 'gain'
}
